import {ContentContainer, useMessage} from '@coolcolduk/ui-mui';
import {ContactMessageState} from '@cp/model';
import {Archive, ExpandMore, MarkEmailRead, MarkEmailUnread} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {useEffect, useRef, useState} from 'react';
import {useListContactMessage} from '../../../hooks/contactMessage/useListContactMessage';
import {useUpdateContactMessage} from '../../../hooks/contactMessage/useUpdateContactMessage';
import {useAuth} from '../../../hooks/users/useAuth';
import {CONTACT_MESSAGE_PAGE_SIZE} from '../../../api/contactMessage/listContactMessage';

dayjs.extend(utc);

const PERMISSION_ERROR_MESSAGE = "You don't have permission to access contact messages.";

function createdAtToDate(createdAt: unknown): dayjs.Dayjs | null {
  if (!createdAt) return null;
  if (typeof createdAt === 'string') return dayjs.utc(createdAt);
  if (
    createdAt &&
    typeof createdAt === 'object' &&
    'toDate' in createdAt &&
    typeof (createdAt as {toDate: () => Date}).toDate === 'function'
  ) {
    return dayjs.utc((createdAt as {toDate: () => Date}).toDate());
  }
  return null;
}

function contactMessageStateLabel(state: ContactMessageState | undefined): string {
  if (state === ContactMessageState.UNREAD) return 'Unread';
  if (state === ContactMessageState.READ) return 'Read';
  if (state === ContactMessageState.ARCHIVE) return 'Archive';
  return 'Unread';
}

function ContactMessageTags({createdAt}: {createdAt: unknown}) {
  const d = createdAtToDate(createdAt);
  if (!d) return null;
  const now = dayjs.utc();
  const within24h = now.diff(d, 'hour') < 24;
  const within7d = now.diff(d, 'day') < 7;
  return (
    <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
      {within24h && <Chip size="small" label="Within 24 hours" color="primary" variant="outlined" />}
      {within7d && !within24h && <Chip size="small" label="Within 7 days" color="secondary" variant="outlined" />}
    </Box>
  );
}

export default function AdminContactUsPage() {
  const {showMessage} = useMessage();
  const showMessageRef = useRef(showMessage);
  showMessageRef.current = showMessage;
  const user = useAuth();
  const uid = user?.id;

  const [showArchived, setShowArchived] = useState(false);
  const {
    data: messages,
    loading,
    error,
    refetch,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    canGoNext,
    canGoPrev,
  } = useListContactMessage(uid, {
    includeUnread: true,
    includeRead: true,
    includeArchived: showArchived,
  });
  const {updateContactMessage, loading: updating} = useUpdateContactMessage();
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      showMessageRef.current(PERMISSION_ERROR_MESSAGE, 6000, 'error');
    }
  }, [error]);

  const handleStateChange = async (messageId: string, newState: ContactMessageState) => {
    setUpdatingId(messageId);
    try {
      await updateContactMessage(messageId, {state: newState});
      await refetch();
    } catch {
      showMessageRef.current(PERMISSION_ERROR_MESSAGE, 6000, 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const isInitialLoad = loading && messages.length === 0;
  if (isInitialLoad) {
    return (
      <ContentContainer data-testid="admin-contact-us-page">
        <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
          <CircularProgress />
        </Box>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer data-testid="admin-contact-us-page">
      <Typography variant="h5" component="h2" sx={{mb: 2}}>
        Contact Us messages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
        Latest {CONTACT_MESSAGE_PAGE_SIZE} messages. Expand to read full message and change state.
      </Typography>
      <FormControlLabel
        control={<Switch checked={showArchived} onChange={(_, checked) => setShowArchived(checked)} />}
        label="Show archive"
        sx={{mb: 2}}
      />
      <ButtonGroup size="small" sx={{mb: 2}}>
        <Button onClick={handleFirstPage} disabled={!canGoPrev}>
          First
        </Button>
        <Button onClick={handlePrevPage} disabled={!canGoPrev}>
          Previous
        </Button>
        <Button onClick={handleNextPage} disabled={!canGoNext}>
          Next
        </Button>
      </ButtonGroup>
      {loading && messages.length > 0 && <LinearProgress sx={{mb: 2}} />}
      {messages.length === 0 ? (
        <Typography color="text.secondary">No contact messages yet.</Typography>
      ) : (
        messages.map((msg) => (
          <Accordion
            key={msg.id}
            sx={{mb: 0.5}}
            onChange={(_, expanded) => {
              if (expanded && (msg.state === ContactMessageState.UNREAD || !msg.state)) {
                handleStateChange(msg.id, ContactMessageState.READ);
              }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%'}}>
                <Typography variant="subtitle2" sx={{flex: '1 1 auto', minWidth: 0}}>
                  {msg.name} — {msg.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {createdAtToDate(msg.createdAt)?.format('YYYY-MM-DD HH:mm') ?? '—'}
                </Typography>
                <ContactMessageTags createdAt={msg.createdAt} />
                <Chip
                  size="small"
                  label={contactMessageStateLabel(msg.state)}
                  variant="outlined"
                  sx={{flexShrink: 0}}
                />
                <Box component="span" onClick={(e) => e.stopPropagation()} sx={{display: 'flex', gap: 0}}>
                  <Tooltip title="Unread">
                    <IconButton
                      component="div"
                      size="small"
                      role="button"
                      disabled={
                        updatingId === msg.id ||
                        updating ||
                        (msg.state ?? ContactMessageState.UNREAD) === ContactMessageState.UNREAD
                      }
                      onClick={() => handleStateChange(msg.id, ContactMessageState.UNREAD)}
                    >
                      <MarkEmailUnread />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Read">
                    <IconButton
                      component="div"
                      size="small"
                      role="button"
                      disabled={
                        updatingId === msg.id ||
                        updating ||
                        (msg.state ?? ContactMessageState.UNREAD) === ContactMessageState.READ
                      }
                      onClick={() => handleStateChange(msg.id, ContactMessageState.READ)}
                    >
                      <MarkEmailRead />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Archive">
                    <IconButton
                      component="div"
                      size="small"
                      role="button"
                      disabled={
                        updatingId === msg.id ||
                        updating ||
                        (msg.state ?? ContactMessageState.UNREAD) === ContactMessageState.ARCHIVE
                      }
                      onClick={() => handleStateChange(msg.id, ContactMessageState.ARCHIVE)}
                    >
                      <Archive />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" component="pre" sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                {msg.message}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </ContentContainer>
  );
}
